import type { GeoPoint, PermissionStatus } from './locationTypes'

/**
 * Injectable seam over the browser Geolocation + Permissions APIs. The
 * `Location` store talks only to this interface, so tests can swap in
 * {@link FakeGeolocationGateway} for deterministic behavior.
 */
export interface GeolocationGateway {
  /** Current OS permission status (no prompt). */
  permissionStatus(): Promise<PermissionStatus>
  /** One-shot location fix (may trigger an OS prompt when `notDetermined`). */
  requestFix(): Promise<GeoPoint>
  /** Subscribe to a live location stream; returns a disposer. */
  watch(onPoint: (point: GeoPoint) => void): () => void
}

/* v8 ignore start — browser geolocation/permissions plumbing is
   jsdom-unreachable; covered by the FakeGeolocationGateway in tests and
   by manual / Playwright runs. */
function pointFrom(position: GeolocationPosition): GeoPoint {
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    // `position.timestamp` is ms since epoch; the cross-platform contract
    // (iOS `timeIntervalSince1970`) is SECONDS — divide.
    timestamp: position.timestamp / 1000,
  }
}
/**
 * Real implementation backed by `navigator.permissions` and
 * `navigator.geolocation`. `permissions.query` is treated as best-effort:
 * a `prompt` / unsupported result maps to `notDetermined`.
 */
export class BrowserGeolocationGateway implements GeolocationGateway {
  async permissionStatus(): Promise<PermissionStatus> {
    if (typeof navigator === 'undefined' || navigator.permissions === undefined) {
      return 'notDetermined'
    }
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' })
      switch (result.state) {
        case 'granted':
          return 'granted'
        case 'denied':
          return 'denied'
        default:
          return 'notDetermined'
      }
    } catch {
      return 'notDetermined'
    }
  }

  requestFix(): Promise<GeoPoint> {
    return new Promise<GeoPoint>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve(pointFrom(position)),
        (error) => reject(error),
      )
    })
  }

  watch(onPoint: (point: GeoPoint) => void): () => void {
    const id = navigator.geolocation.watchPosition((position) => {
      onPoint(pointFrom(position))
    })
    return () => navigator.geolocation.clearWatch(id)
  }
}
/* v8 ignore stop */

export interface FakeGeolocationConfig {
  readonly permission: PermissionStatus
  readonly fix: GeoPoint
}

/**
 * Deterministic gateway for tests. `emit` pushes a point to every active
 * `watch` listener, simulating live movement.
 */
export class FakeGeolocationGateway implements GeolocationGateway {
  private readonly watchers = new Set<(point: GeoPoint) => void>()
  private readonly config: FakeGeolocationConfig

  constructor(config: FakeGeolocationConfig) {
    this.config = config
  }

  async permissionStatus(): Promise<PermissionStatus> {
    return this.config.permission
  }

  async requestFix(): Promise<GeoPoint> {
    return this.config.fix
  }

  watch(onPoint: (point: GeoPoint) => void): () => void {
    this.watchers.add(onPoint)
    return () => {
      this.watchers.delete(onPoint)
    }
  }

  /** Test helper: deliver `point` to all active watchers. */
  async emit(point: GeoPoint): Promise<void> {
    for (const w of this.watchers) w(point)
  }
}
